import { useTech } from './tech'
import { isPast, parseISO } from 'date-fns'
import { minBy } from 'lodash-es'

export const useFreeWeekend = defineStore('freeWeekend', () => {
  const data = ref({
    is_enabled: false,
    is_active: false
  })

  const tech = useTech()
  const { $api } = useNuxtApp()

  const loaded = ref(false)
  const isEnded = computed(() => data.value.is_enabled && isPast(data.value.ended_at))
  const isMockExamActive = computed(() => data.value.is_enabled && data.value.mock_exam)
  const certifications = computed(() => data.value.certifications)
  const trainings = computed(() => data.value.trainings)

  const activeFreeWeekendCert = computed(() => data.value.is_active ? data.value.tech : null)
  const activeFreeWeekendBanner = computed(() => {
    const currentBannerComponent = {
      javascript: 'FreeWeekendJavaScriptOfferBanner',
      angular: 'FreeWeekendOfferBanner'
    }[activeFreeWeekendCert.value]

    if (!currentBannerComponent) {
      return 'FreeWeekendOfferBanner'
    }

    return currentBannerComponent
  })

  function isCertificationForFreeWeekend (certification) {
    return certification && certifications.value.map(item => item.id).includes(certification.id)
  }

  function isTrainingForFreeWeekend (training) {
    return training && trainings.value.map(item => item.id).includes(training.id)
  }

  async function startTrialExam (certification) {
    const exam = await getMockExam(certification.slug)

    // navigate to exam
    navigateTo(`/${tech.id}/trial-exams/${exam.id}/terms`)
  }

  async function getMockExam (certSlug) {
    try {
      const { data: response } = await $api.post('/trial-exams', {
        certification: certSlug,
        free_weekend: tech.id
      })

      return response
    } catch (e) {
      console.log('Error fetching mock exam', e)
    }
  }

  async function init () {
    try {
      const { data: response } = await useFetch(`/api/free-weekend/${tech.id}`)

      const selectedFreeWeekend = response.value?.data?.find(item => item.is_active)

      if (selectedFreeWeekend) {
        data.value = parse(selectedFreeWeekend)
      } else if (response.length > 0) {
        const now = new Date()
        const closestEvent = minBy(response, (event) => {
          const eventDate = new Date(event.started_at)
          return Math.abs(eventDate - now)
        })

        data.value = parse(closestEvent)
      }

      loaded.value = true
    } catch (e) {
      console.error(e)
    }
  }

  async function getRecentFw () {
    try {
      const { data: freeWeekends } = await $fetch('/api/free-weekends')
      const enabledFWs = freeWeekends.filter(
        (e) => e.is_enabled
      )

      const active = enabledFWs.find((w) => w.is_active)

      if (active) return active

      const now = new Date()
      const future = enabledFWs.filter(e => new Date(e.started_at) >= now)
      const past = enabledFWs.filter(e => new Date(e.started_at) < now)
      const sortByClosest = arr =>
        [...arr].sort((a, b) =>
          Math.abs(new Date(a.started_at) - now) - Math.abs(new Date(b.started_at) - now)
        )

      const target =
        sortByClosest(future)[0] || sortByClosest(past)[0]

      return target
    } catch (e) {
      console.error(e)
    }
  }

  function parse (response) {
    return {
      ...response,
      start_date: parseISO(response.started_at),
      end_date: parseISO(response.ended_at)
    }
  }

  return {
    data,
    loaded,
    init,
    getRecentFw,
    activeFreeWeekendCert,
    activeFreeWeekendBanner,
    isEnded,
    isMockExamActive,
    getMockExam,
    certifications,
    trainings,
    isCertificationForFreeWeekend,
    isTrainingForFreeWeekend,
    startTrialExam
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useFreeWeekend, import.meta.hot))
}
